import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import { AddressAutocomplete } from '../../components/AddressAutocomplete';
import { toast } from 'sonner';
import axios from 'axios';
import { getApiErrorMessage } from '../../utils/errorHelper';
import {
  ArrowLeft, 
  ArrowRight, 
  ChevronRight,
  Utensils, 
  Zap, 
  ShoppingBasket, 
  Croissant, 
  Beef, 
  Wine, 
  Fish, 
  MilkOff,
  Check,
  MapPin,
  Clock,
  CreditCard,
  User,
  FileText,
  CheckCircle2,
  Info,
  Upload,
  Phone,
  Mail,
  Globe,
  Building2,
  Calendar,
  MessageCircle,
  Video,
  Loader2,
  Eye,
  EyeOff,
  RotateCcw,
  X,
  Image as ImageIcon,
  Gift
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const ONBOARDING_SESSION_KEY = 'lemo_onboarding_session';

// Configuration des types de commerce
const SHOP_TYPES = [
  { id: 'restaurant_traditionnel', label: 'Restaurant traditionnel', icon: Utensils, color: 'bg-orange-100 text-orange-600' },
  { id: 'restaurant_rapide', label: 'Restaurant rapide', icon: Zap, color: 'bg-yellow-100 text-yellow-600' },
  { id: 'epicerie', label: 'Épicerie', icon: ShoppingBasket, color: 'bg-green-100 text-green-600' },
  { id: 'boulangerie', label: 'Boulangerie', icon: Croissant, color: 'bg-amber-100 text-amber-600' },
  { id: 'boucherie', label: 'Boucherie', icon: Beef, color: 'bg-red-100 text-red-600' },
  { id: 'epicerie_fine', label: 'Épicerie fine', icon: Wine, color: 'bg-purple-100 text-purple-600' },
  { id: 'poissonnier', label: 'Poissonnier', icon: Fish, color: 'bg-blue-100 text-blue-600' },
  { id: 'fromager', label: 'Fromager', icon: MilkOff, color: 'bg-indigo-100 text-indigo-600' },
];

// Configuration des étapes du wizard
const WIZARD_STEPS = [
  { id: 'shop_type', label: 'Type', icon: Building2 },
  { id: 'quick_start', label: 'Démarrage', icon: Zap },
  { id: 'shop_details', label: 'Détails', icon: FileText },
  { id: 'basic_info', label: 'Infos', icon: MapPin },
  { id: 'operations', label: 'Opérations', icon: Clock },
  { id: 'account', label: 'Compte', icon: User },
  { id: 'documents', label: 'Documents', icon: FileText },
  { id: 'confirmation', label: 'Confirmation', icon: CheckCircle2 },
];

// Jours de la semaine
const DAYS = [
  { id: 'monday', label: 'Lundi' },
  { id: 'tuesday', label: 'Mardi' },
  { id: 'wednesday', label: 'Mercredi' },
  { id: 'thursday', label: 'Jeudi' },
  { id: 'friday', label: 'Vendredi' },
  { id: 'saturday', label: 'Samedi' },
  { id: 'sunday', label: 'Dimanche' },
];

// Moyens de paiement
const PAYMENT_METHODS = [
  { id: 'card', label: 'Carte bancaire', icon: CreditCard },
  { id: 'cash', label: 'Especes', icon: '💵' },
  { id: 'ticket_restaurant_paper', label: 'Ticket restaurant papier', icon: '🎫' },
  { id: 'ticket_restaurant_card', label: 'Carte ticket restaurant', icon: '💳' },
  { id: 'american_express', label: 'American Express', icon: '💎' },
  { id: 'bitcoin', label: 'Bitcoin', icon: '₿' },
];

// Questions conditionnelles par type de commerce
const CONDITIONAL_QUESTIONS = {
  restaurant_traditionnel: [
    { id: 'cuisine_type', label: 'Type de cuisine', type: 'select', required: true, options: ['Française', 'Italienne', 'Asiatique', 'Méditerranéenne', 'Autre'] },
    { id: 'sells_alcohol', label: 'Vente d\'alcool', type: 'boolean', required: true },
  ],
  restaurant_rapide: [
    { id: 'prep_time', label: 'Temps de préparation moyen', type: 'select', required: true, options: ['5 min', '10 min', '15 min', '20 min+'] },
    { id: 'sells_alcohol', label: 'Vente d\'alcool', type: 'boolean', required: true },
  ],
  epicerie: [
    { id: 'avg_basket', label: 'Panier moyen estimé', type: 'select', required: true, options: ['Moins de 20€', '20€ - 40€', '40€ - 60€', '60€+'] },
    { id: 'sells_alcohol', label: 'Vente d\'alcool', type: 'boolean', required: true },
    { id: 'night_opening', label: 'Ouverture de nuit', type: 'boolean', required: true },
  ],
  boulangerie: [
    { id: 'opening_hour', label: 'Heure d\'ouverture habituelle', type: 'select', required: true, options: ['5h', '6h', '7h', '8h'] },
    { id: 'makes_on_site', label: 'Fabrique sur place', type: 'boolean', required: true },
    { id: 'also_sells', label: 'Vend aussi', type: 'multiselect', required: false, options: ['Café', 'Pâtisseries', 'Sandwichs', 'Viennoiseries'] },
    { id: 'sells_alcohol', label: 'Vente d\'alcool', type: 'boolean', required: true },
  ],
  boucherie: [
    { id: 'processed_products', label: 'Produits transformés', type: 'multiselect', required: false, options: ['Saucisses', 'Merguez', 'Brochettes', 'Plats préparés'] },
    { id: 'open_sunday', label: 'Ouvert le dimanche', type: 'boolean', required: true },
    { id: 'sells_alcohol', label: 'Vente d\'alcool', type: 'boolean', required: true },
  ],
  fromager: [
    { id: 'dairy_products', label: 'Produits laitiers complémentaires', type: 'multiselect', required: false, options: ['Beurre', 'Crème', 'Yaourts', 'Lait'] },
    { id: 'sells_alcohol', label: 'Vente d\'alcool', type: 'boolean', required: true },
  ],
  poissonnier: [
    { id: 'refrigerated_package', label: 'Colis réfrigéré disponible', type: 'boolean', required: true },
    { id: 'on_site_prep', label: 'Préparation sur place', type: 'multiselect', required: false, options: ['Filetage', 'Écaillage', 'Découpe'] },
    { id: 'sells_frozen_smoked', label: 'Vente fumé / congelé', type: 'boolean', required: true },
    { id: 'sells_alcohol', label: 'Vente d\'alcool', type: 'boolean', required: true },
  ],
  epicerie_fine: [
    { id: 'specialties', label: 'Type de spécialités', type: 'multiselect', required: true, options: ['Vins', 'Épicerie du monde', 'Caviar', 'Truffes', 'Huiles & vinaigres'] },
    { id: 'sells_cheese_charcuterie', label: 'Vente fromages / charcuterie', type: 'boolean', required: true },
    { id: 'sells_alcohol', label: 'Vente d\'alcool', type: 'boolean', required: true },
  ],
};

export default function RestaurantOnboarding() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setAuth } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [onboardingId, setOnboardingId] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showResumeModal, setShowResumeModal] = useState(false);
  const [savedSession, setSavedSession] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [referralCode, setReferralCode] = useState(searchParams.get('ref') || '');
  const [referralValid, setReferralValid] = useState(null);
  
  // Form data
  const [formData, setFormData] = useState({
    // Step 0: Shop type
    shop_type: null,
    
    // Step 1: Quick start
    shop_name: '',
    quick_address: '',
    contact_first_name: '',
    contact_email: '',
    
    // Step 2: Shop details (conditional)
    shop_details: {},
    
    // Step 3: Basic info
    name: '',
    address: '',
    zipcode: '',
    city: '',
    latitude: null,
    longitude: null,
    phone: '',
    email: '',
    logo: null,
    cover_image: null,
    website: '',
    siret: '',
    
    // Step 4: Operations
    opening_hours: {},
    same_hours_all_week: false,
    accepted_payment_methods: ['card'],
    closed_days: [],
    default_open: '09:00',
    default_close: '19:00',
    
    // Step 5: Account
    manager_first_name: '',
    manager_last_name: '',
    manager_email: '',
    manager_phone: '',
    manager_role: '',
    password: '',
    confirm_password: '',
    
    // Step 6: Documents
    alcohol_license: null,
    other_documents: [],
    
    // Step 7: Confirmation
    confirmed: false,
    onboarding_call_type: null
  });

  const progress = ((currentStep + 1) / WIZARD_STEPS.length) * 100;

  // Validate referral code from URL
  useEffect(() => {
    const validateRefCode = async () => {
      const code = searchParams.get('ref');
      if (!code) return;
      try {
        const res = await axios.get(`${API_URL}/api/referral/validate/${code}`);
        if (res.data.valid) {
          setReferralCode(code.toUpperCase());
          setReferralValid(res.data);
        }
      } catch {
        setReferralValid(null);
      }
    };
    validateRefCode();
  }, [searchParams]);

  // Check for existing session on mount
  useEffect(() => {
    const checkExistingSession = async () => {
      setLoading(true);
      const savedSessionId = localStorage.getItem(ONBOARDING_SESSION_KEY);
      
      if (savedSessionId) {
        try {
          const response = await axios.get(`${API_URL}/api/onboarding/resume/${savedSessionId}`);
          if (response.data.can_resume) {
            setSavedSession(response.data.onboarding);
            setShowResumeModal(true);
            setLoading(false);
            return;
          }
        } catch (error) {
          // Session expired or not found, clear it
          localStorage.removeItem(ONBOARDING_SESSION_KEY);
        }
      }
      
      // Start new session
      await startNewSession();
    };
    
    checkExistingSession();
  }, []);

  const startNewSession = async () => {
    try {
      const response = await axios.post(`${API_URL}/api/onboarding/start`);
      setOnboardingId(response.data.id);
      localStorage.setItem(ONBOARDING_SESSION_KEY, response.data.id);
      setLoading(false);
    } catch (error) {
      console.error('Error starting onboarding:', error);
      toast.error('Erreur lors de l\'initialisation');
      setLoading(false);
    }
  };

  const handleResumeSession = () => {
    if (!savedSession) return;
    
    // Restore form data from saved session
    setOnboardingId(savedSession.id);
    setCurrentStep(savedSession.current_step || 0);
    
    // Restore all saved data
    setFormData(prev => ({
      ...prev,
      shop_type: savedSession.shop_type,
      shop_name: savedSession.quick_start?.shop_name || '',
      quick_address: savedSession.quick_start?.address || '',
      contact_first_name: savedSession.quick_start?.contact_first_name || '',
      contact_email: savedSession.quick_start?.contact_email || '',
      shop_details: savedSession.shop_details || {},
      name: savedSession.basic_info?.name || '',
      address: savedSession.basic_info?.address || '',
      zipcode: savedSession.basic_info?.zipcode || '',
      city: savedSession.basic_info?.city || '',
      latitude: savedSession.basic_info?.latitude,
      longitude: savedSession.basic_info?.longitude,
      phone: savedSession.basic_info?.phone || '',
      email: savedSession.basic_info?.email || '',
      logo: savedSession.basic_info?.logo,
      cover_image: savedSession.basic_info?.cover_image,
      website: savedSession.basic_info?.website || '',
      siret: savedSession.basic_info?.siret || '',
      opening_hours: savedSession.operations?.opening_hours || {},
      accepted_payment_methods: savedSession.operations?.accepted_payment_methods || ['card'],
      manager_first_name: savedSession.account?.manager_first_name || '',
      manager_last_name: savedSession.account?.manager_last_name || '',
      manager_email: savedSession.account?.manager_email || '',
      manager_phone: savedSession.account?.manager_phone || '',
      manager_role: savedSession.account?.manager_role || '',
    }));
    
    setShowResumeModal(false);
    toast.success('Session reprise avec succès !');
  };

  const handleStartFresh = async () => {
    localStorage.removeItem(ONBOARDING_SESSION_KEY);
    setSavedSession(null);
    setShowResumeModal(false);
    await startNewSession();
  };

  // Track step view for analytics
  const trackStepView = useCallback(async (step) => {
    if (!onboardingId) return;
    try {
      await axios.post(`${API_URL}/api/onboarding/${onboardingId}/track-step-view?step=${step}`);
    } catch (error) {
      // Silent fail for tracking
    }
  }, [onboardingId]);

  // Track when user leaves the page
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (onboardingId && currentStep > 0) {
        // Use sendBeacon for reliable tracking on page close
        navigator.sendBeacon(
          `${API_URL}/api/onboarding/${onboardingId}/track-abandon?step=${currentStep}`,
          ''
        );
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [onboardingId, currentStep]);

  // Track step views
  useEffect(() => {
    if (onboardingId && !loading) {
      trackStepView(currentStep);
    }
  }, [currentStep, onboardingId, loading, trackStepView]);

  // Handle file upload
  const handleFileUpload = async (file, type) => {
    if (type === 'logo') setUploadingLogo(true);
    else if (type === 'cover') setUploadingCover(true);
    
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);
    
    try {
      const response = await axios.post(`${API_URL}/api/upload?optimize=true`, formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (response.data.url) {
        const imageUrl = response.data.url.startsWith('http') 
          ? response.data.url 
          : `${API_URL}${response.data.url}`;
        
        if (type === 'logo') {
          updateFormData('logo', imageUrl);
          toast.success('Logo téléchargé avec succès');
        } else if (type === 'cover') {
          updateFormData('cover_image', imageUrl);
          toast.success('Image de couverture téléchargée');
        }
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Erreur lors du téléchargement');
    } finally {
      if (type === 'logo') setUploadingLogo(false);
      else if (type === 'cover') setUploadingCover(false);
    }
  };

  // Auto-save step data
  const saveStep = useCallback(async (step, data) => {
    if (!onboardingId) return;
    try {
      await axios.put(`${API_URL}/api/onboarding/${onboardingId}/step/${step}`, {
        step,
        data
      });
    } catch (error) {
      console.error('Error saving step:', error);
    }
  }, [onboardingId]);

  // Update form data helper
  const updateFormData = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Validate current step
  const validateStep = () => {
    switch (currentStep) {
      case 0:
        if (!formData.shop_type) {
          toast.error('Veuillez sélectionner un type de commerce');
          return false;
        }
        break;
      case 1:
        if (!formData.shop_name || !formData.contact_email) {
          toast.error('Veuillez remplir tous les champs obligatoires');
          return false;
        }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contact_email)) {
          toast.error('Veuillez entrer un email valide');
          return false;
        }
        break;
      case 3:
        if (!formData.name || !formData.address || !formData.city || !formData.phone || !formData.email) {
          toast.error('Veuillez remplir tous les champs obligatoires');
          return false;
        }
        break;
      case 4:
        if (formData.accepted_payment_methods.length === 0) {
          toast.error('Veuillez sélectionner au moins un moyen de paiement');
          return false;
        }
        break;
      case 5:
        if (!formData.manager_first_name || !formData.manager_last_name || 
            !formData.manager_email || !formData.manager_phone || 
            !formData.manager_role || !formData.password) {
          toast.error('Veuillez remplir tous les champs obligatoires');
          return false;
        }
        if (formData.password !== formData.confirm_password) {
          toast.error('Les mots de passe ne correspondent pas');
          return false;
        }
        if (formData.password.length < 8) {
          toast.error('Le mot de passe doit contenir au moins 8 caractères');
          return false;
        }
        break;
      case 7:
        if (!formData.confirmed) {
          toast.error('Veuillez confirmer les informations');
          return false;
        }
        break;
      default:
        break;
    }
    return true;
  };

  // Handle next step
  const handleNext = async () => {
    if (!validateStep()) return;
    
    // Save current step
    const stepData = getStepData(currentStep);
    await saveStep(currentStep, stepData);
    
    if (currentStep < WIZARD_STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
      window.scrollTo(0, 0);
    }
  };

  // Handle previous step
  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
      window.scrollTo(0, 0);
    }
  };

  // Get step data for saving
  const getStepData = (step) => {
    switch (step) {
      case 0: return { shop_type: formData.shop_type };
      case 1: return { 
        shop_name: formData.shop_name,
        address: formData.quick_address,
        contact_first_name: formData.contact_first_name,
        contact_email: formData.contact_email
      };
      case 2: return formData.shop_details;
      case 3: return {
        name: formData.name,
        address: formData.address,
        zipcode: formData.zipcode,
        city: formData.city,
        latitude: formData.latitude,
        longitude: formData.longitude,
        phone: formData.phone,
        email: formData.email,
        website: formData.website,
        siret: formData.siret
      };
      case 4: return {
        opening_hours: formData.opening_hours,
        accepted_payment_methods: formData.accepted_payment_methods
      };
      case 5: return {
        manager_first_name: formData.manager_first_name,
        manager_last_name: formData.manager_last_name,
        manager_email: formData.manager_email,
        manager_phone: formData.manager_phone,
        manager_role: formData.manager_role
      };
      case 6: return {
        alcohol_license: formData.alcohol_license,
        other_documents: formData.other_documents
      };
      default: return {};
    }
  };

  // Build opening hours object
  const buildOpeningHours = () => {
    const hours = {};
    if (formData.same_hours_all_week) {
      DAYS.forEach(day => {
        hours[day.id] = formData.closed_days.includes(day.id)
          ? { closed: true }
          : { open: formData.default_open, close: formData.default_close };
      });
    } else {
      DAYS.forEach(day => {
        hours[day.id] = formData.closed_days.includes(day.id)
          ? { closed: true }
          : { 
              open: formData.opening_hours[day.id]?.open || '09:00', 
              close: formData.opening_hours[day.id]?.close || '19:00' 
            };
      });
    }
    return hours;
  };

  // Final submit
  const handleSubmit = async () => {
    if (!validateStep()) return;
    
    setSubmitting(true);
    try {
      const payload = {
        shop_type: formData.shop_type,
        shop_details: formData.shop_details,
        name: formData.name || formData.shop_name,
        address: formData.address || formData.quick_address,
        zipcode: formData.zipcode,
        city: formData.city,
        latitude: formData.latitude,
        longitude: formData.longitude,
        phone: formData.phone,
        email: formData.email || formData.contact_email,
        logo: formData.logo,
        cover_image: formData.cover_image,
        website: formData.website || null,
        siret: formData.siret || null,
        opening_hours: buildOpeningHours(),
        accepted_payment_methods: formData.accepted_payment_methods,
        manager_first_name: formData.manager_first_name || formData.contact_first_name,
        manager_last_name: formData.manager_last_name,
        manager_email: formData.manager_email || formData.contact_email,
        manager_phone: formData.manager_phone || formData.phone,
        manager_role: formData.manager_role || 'owner',
        password: formData.password,
        alcohol_license: formData.alcohol_license,
        other_documents: formData.other_documents,
        onboarding_call_type: formData.onboarding_call_type,
        confirmed: formData.confirmed,
        referral_code: referralCode || null
      };

      const response = await axios.post(`${API_URL}/api/onboarding/${onboardingId}/complete`, payload);
      
      if (response.data.success) {
        // Clear saved session on success
        localStorage.removeItem(ONBOARDING_SESSION_KEY);
        
        // Auto login - httpOnly cookie is already set by the backend response
        if (response.data.user) {
          setAuth(null, response.data.user);
        }
        
        toast.success('Inscription réussie ! Votre commerce est en attente de validation.');
        navigate('/restaurant-dashboard/settings');
      }
    } catch (error) {
      console.error('Error completing onboarding:', error);
      toast.error(getApiErrorMessage(error, "Erreur lors de l'inscription"));
    } finally {
      setSubmitting(false);
    }
  };

  // Copy quick start data to basic info
  useEffect(() => {
    if (currentStep === 3 && !formData.name) {
      setFormData(prev => ({
        ...prev,
        name: prev.shop_name,
        email: prev.contact_email,
        manager_first_name: prev.contact_first_name,
        manager_email: prev.contact_email
      }));
    }
  }, [currentStep, formData.name, formData.shop_name, formData.contact_email, formData.contact_first_name]);

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <ShopTypeStep 
            selectedShopType={formData.shop_type} 
            setSelectedShopType={(type) => updateFormData('shop_type', type)} 
          />
        );
      case 1:
        return (
          <QuickStartStep 
            formData={formData}
            updateFormData={updateFormData}
          />
        );
      case 2:
        return (
          <ShopDetailsStep 
            shopType={formData.shop_type}
            answers={formData.shop_details}
            setAnswers={(answers) => updateFormData('shop_details', answers)}
          />
        );
      case 3:
        return (
          <BasicInfoStep 
            formData={formData}
            updateFormData={updateFormData}
            onUpload={handleFileUpload}
            uploadingLogo={uploadingLogo}
            uploadingCover={uploadingCover}
          />
        );
      case 4:
        return (
          <OperationsStep 
            formData={formData}
            updateFormData={updateFormData}
          />
        );
      case 5:
        return (
          <AccountStep 
            formData={formData}
            updateFormData={updateFormData}
            showPassword={showPassword}
            setShowPassword={setShowPassword}
          />
        );
      case 6:
        return <DocumentsStep formData={formData} updateFormData={updateFormData} />;
      case 7:
        return (
          <ConfirmationStep 
            formData={formData}
            updateFormData={updateFormData}
          />
        );
      default:
        return null;
    }
  };

  // Loading screen
  if (loading && !showResumeModal) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center" data-testid="onboarding-loading">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-emerald-500 animate-spin mx-auto mb-4" />
          <p className="text-slate-600">Chargement...</p>
        </div>
      </div>
    );
  }

  // Resume session modal
  if (showResumeModal && savedSession) {
    const lastStep = WIZARD_STEPS[savedSession.current_step] || WIZARD_STEPS[0];
    const completedCount = savedSession.completed_steps?.length || 0;
    
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4" data-testid="onboarding-resume-modal">
        <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6 border border-slate-100">
          <div className="text-center mb-6">
            <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
              <RotateCcw className="w-8 h-8 text-emerald-600" />
            </div>
            <h2 className="text-xl font-bold text-slate-900 mb-2">
              Reprendre votre inscription ?
            </h2>
            <p className="text-slate-600">
              Nous avons trouvé une inscription en cours pour 
              {savedSession.quick_start?.shop_name && (
                <strong className="text-slate-900"> {savedSession.quick_start.shop_name}</strong>
              )}
            </p>
          </div>

          <div className="bg-slate-50 rounded-xl p-4 mb-6">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm text-slate-500">Progression</span>
              <span className="text-sm font-medium text-emerald-600">
                {completedCount}/{WIZARD_STEPS.length - 1} étapes
              </span>
            </div>
            <div className="w-full h-2 bg-slate-200 rounded-full overflow-hidden">
              <div 
                className="h-full bg-emerald-500 rounded-full transition-all"
                style={{ width: `${(completedCount / (WIZARD_STEPS.length - 1)) * 100}%` }}
              />
            </div>
            <div className="mt-3 flex items-center gap-2 text-sm text-slate-600">
              <MapPin className="w-4 h-4" />
              Dernière étape: <strong>{lastStep.label}</strong>
            </div>
          </div>

          <div className="space-y-3">
            <Button 
              onClick={handleResumeSession}
              className="w-full bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl h-12"
              data-testid="resume-session-btn"
            >
              <RotateCcw className="w-4 h-4 mr-2" />
              Reprendre mon inscription
            </Button>
            <Button 
              onClick={handleStartFresh}
              variant="outline"
              className="w-full rounded-xl h-12 border-slate-200"
              data-testid="start-fresh-btn"
            >
              <X className="w-4 h-4 mr-2" />
              Recommencer à zéro
            </Button>
          </div>

          <p className="text-xs text-slate-400 text-center mt-4">
            Vos données sont automatiquement sauvegardées pendant 7 jours
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col" data-testid="restaurant-onboarding">
      {/* Header */}
      <header className="bg-white border-b border-slate-100 sticky top-0 z-40">
        <div className="max-w-2xl mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2" data-testid="wizard-logo">
              <img src="/logo.png" alt="LEMO" className="w-20 h-20 -my-4 object-contain" />
              <span className="font-bold text-xl text-slate-900">LEMO</span>
            </Link>
            <Link 
              to="/" 
              className="text-slate-500 hover:text-slate-700 flex items-center gap-2 text-sm"
              data-testid="wizard-back-link"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="hidden sm:inline">Retour</span>
            </Link>
          </div>
        </div>
      </header>

      {/* Progress Bar */}
      <div className="bg-white border-b border-slate-100">
        <div className="max-w-2xl mx-auto px-4 py-4">
          {/* Breadcrumbs */}
          <div className="flex items-center gap-2 text-sm text-slate-500 mb-3 overflow-x-auto pb-1">
            {WIZARD_STEPS.map((step, index) => (
              <div key={step.id} className="flex items-center gap-2 whitespace-nowrap">
                <span 
                  className={`flex items-center gap-1.5 ${
                    index === currentStep 
                      ? 'text-emerald-600 font-medium' 
                      : index < currentStep 
                        ? 'text-emerald-500' 
                        : 'text-slate-400'
                  }`}
                >
                  {index < currentStep ? (
                    <Check className="w-4 h-4" />
                  ) : (
                    <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs ${
                      index === currentStep 
                        ? 'bg-emerald-500 text-white' 
                        : 'bg-slate-200 text-slate-500'
                    }`}>
                      {index + 1}
                    </span>
                  )}
                  <span className="hidden sm:inline">{step.label}</span>
                </span>
                {index < WIZARD_STEPS.length - 1 && (
                  <ChevronRight className="w-4 h-4 text-slate-300" />
                )}
              </div>
            ))}
          </div>
          
          {/* Progress Bar */}
          <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
            <div 
              className="h-full bg-emerald-500 transition-all duration-500 ease-out rounded-full"
              style={{ width: `${progress}%` }}
            />
          </div>
          <p className="text-xs text-slate-500 mt-2 text-right">{Math.round(progress)}% complété</p>
        </div>
      </div>

      {/* Main Content */}
      <main className="flex-1 py-6 md:py-10">
        <div className="max-w-2xl mx-auto px-4">
          {/* Referral Banner */}
          {referralCode && referralValid && (
            <div className="mb-6 p-4 bg-emerald-50 border border-emerald-200 rounded-xl flex items-center gap-3" data-testid="referral-banner">
              <Gift className="w-5 h-5 text-emerald-600 shrink-0" />
              <div>
                <p className="text-sm font-medium text-emerald-800">
                  Parraine par <strong>{referralValid.referrer_name}</strong>
                </p>
                <p className="text-xs text-emerald-600">{referralValid.benefit}</p>
              </div>
            </div>
          )}
          {renderStepContent()}
        </div>
      </main>

      {/* Action Bar */}
      <div className="sticky bottom-0 bg-white border-t border-slate-100 p-4 md:static md:bg-transparent md:border-0">
        <div className="max-w-2xl mx-auto flex items-center justify-between gap-4">
          <Button
            variant="outline"
            onClick={handlePrev}
            disabled={currentStep === 0}
            className="h-12 px-6 rounded-full"
            data-testid="wizard-prev-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Précédent
          </Button>
          
          {currentStep < WIZARD_STEPS.length - 1 ? (
            <Button
              onClick={handleNext}
              disabled={loading}
              className="h-12 px-8 bg-emerald-500 hover:bg-emerald-600 rounded-full flex-1 md:flex-none"
              data-testid="wizard-next-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                <>
                  Continuer
                  <ArrowRight className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={submitting || !formData.confirmed}
              className="h-12 px-8 bg-emerald-500 hover:bg-emerald-600 rounded-full flex-1 md:flex-none"
              data-testid="wizard-submit-btn"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                <>
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Finaliser l'inscription
                </>
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

// === STEP COMPONENTS ===

function ShopTypeStep({ selectedShopType, setSelectedShopType }) {
  return (
    <div className="space-y-6" data-testid="step-shop-type">
      <div className="text-center">
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2">
          Quel type de commerce ?
        </h1>
        <p className="text-slate-500">
          Sélectionnez le type qui correspond le mieux à votre activité
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
        {SHOP_TYPES.map((type) => {
          const Icon = type.icon;
          const isSelected = selectedShopType === type.id;
          return (
            <button
              key={type.id}
              onClick={() => setSelectedShopType(type.id)}
              className={`flex flex-col items-center justify-center p-4 md:p-6 border-2 rounded-xl cursor-pointer transition-all ${
                isSelected 
                  ? 'border-emerald-500 bg-emerald-50 ring-1 ring-emerald-500' 
                  : 'border-slate-200 bg-white hover:border-emerald-200 hover:bg-emerald-50/50'
              }`}
              data-testid={`shop-type-${type.id}`}
            >
              <div className={`w-12 h-12 md:w-14 md:h-14 rounded-xl ${type.color} flex items-center justify-center mb-3`}>
                <Icon className="w-6 h-6 md:w-7 md:h-7" />
              </div>
              <span className={`text-sm md:text-base font-medium text-center ${isSelected ? 'text-emerald-700' : 'text-slate-700'}`}>
                {type.label}
              </span>
              {isSelected && (
                <div className="mt-2 w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center">
                  <Check className="w-3 h-3 text-white" />
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function QuickStartStep({ formData, updateFormData }) {
  return (
    <div className="space-y-6" data-testid="step-quick-start">
      <div className="text-center">
        <div className="w-16 h-16 rounded-2xl bg-emerald-100 flex items-center justify-center mx-auto mb-4">
          <Zap className="w-8 h-8 text-emerald-600" />
        </div>
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2">
          Démarrage rapide
        </h1>
        <p className="text-slate-500">
          Cela prend 2 minutes. Vous pourrez compléter les détails plus tard.
        </p>
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 p-6 space-y-5">
        <div>
          <Label className="text-sm font-medium text-slate-700">
            Nom du commerce <span className="text-red-500">*</span>
          </Label>
          <Input
            type="text"
            value={formData.shop_name}
            onChange={(e) => updateFormData('shop_name', e.target.value)}
            placeholder="Ex: Boulangerie du Marché"
            className="mt-1.5 h-12 rounded-xl"
            data-testid="input-shop-name"
          />
        </div>

        <div>
          <Label className="text-sm font-medium text-slate-700">
            Adresse <span className="text-red-500">*</span>
          </Label>
          <div className="relative mt-1.5">
            <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 z-10" />
            <Input
              type="text"
              value={formData.quick_address}
              onChange={(e) => updateFormData('quick_address', e.target.value)}
              placeholder="Commencez à taper votre adresse..."
              className="h-12 rounded-xl pl-10"
              data-testid="input-quick-address"
            />
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-sm font-medium text-slate-700">
              Prénom du contact <span className="text-red-500">*</span>
            </Label>
            <Input
              type="text"
              value={formData.contact_first_name}
              onChange={(e) => updateFormData('contact_first_name', e.target.value)}
              placeholder="Jean"
              className="mt-1.5 h-12 rounded-xl"
              data-testid="input-contact-name"
            />
          </div>
          <div>
            <Label className="text-sm font-medium text-slate-700">
              Email <span className="text-red-500">*</span>
            </Label>
            <Input
              type="email"
              value={formData.contact_email}
              onChange={(e) => updateFormData('contact_email', e.target.value)}
              placeholder="jean@example.com"
              className="mt-1.5 h-12 rounded-xl"
              data-testid="input-contact-email"
            />
          </div>
        </div>
      </div>

      <div className="bg-emerald-50 rounded-xl p-4 flex items-start gap-3">
        <Info className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
        <p className="text-sm text-emerald-700">
          Ces informations nous permettent de créer votre espace. Vous pourrez modifier tous les détails ensuite.
        </p>
      </div>
    </div>
  );
}

function ShopDetailsStep({ shopType, answers, setAnswers }) {
  const questions = CONDITIONAL_QUESTIONS[shopType] || [];
  const shopTypeLabel = SHOP_TYPES.find(t => t.id === shopType)?.label || 'Commerce';

  const handleAnswerChange = (questionId, value) => {
    setAnswers({ ...answers, [questionId]: value });
  };

  const handleMultiSelectToggle = (questionId, option) => {
    const current = answers[questionId] || [];
    if (current.includes(option)) {
      setAnswers({ ...answers, [questionId]: current.filter(o => o !== option) });
    } else {
      setAnswers({ ...answers, [questionId]: [...current, option] });
    }
  };

  if (questions.length === 0) {
    return (
      <div className="space-y-6 text-center" data-testid="step-shop-details">
        <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto">
          <Check className="w-8 h-8 text-emerald-600" />
        </div>
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900">
          Parfait !
        </h1>
        <p className="text-slate-500">
          Pas de questions supplémentaires pour ce type de commerce.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="step-shop-details">
      <div className="text-center">
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2">
          Détails {shopTypeLabel}
        </h1>
        <p className="text-slate-500">
          Quelques questions spécifiques à votre activité
        </p>
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 p-6 space-y-6">
        {questions.map((question) => (
          <div key={question.id} className="space-y-2">
            <Label className="text-sm font-medium text-slate-700">
              {question.label}
              {question.required && <span className="text-red-500 ml-1">*</span>}
            </Label>

            {question.type === 'select' && (
              <select
                value={answers[question.id] || ''}
                onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                className="w-full h-12 rounded-xl border border-slate-200 px-4 bg-white focus:border-emerald-500 focus:ring-emerald-500"
                data-testid={`select-${question.id}`}
              >
                <option value="">Sélectionnez une option</option>
                {question.options.map((opt) => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            )}

            {question.type === 'boolean' && (
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => handleAnswerChange(question.id, true)}
                  className={`flex-1 h-12 rounded-xl border-2 font-medium transition-all ${
                    answers[question.id] === true
                      ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                      : 'border-slate-200 text-slate-600 hover:border-slate-300'
                  }`}
                  data-testid={`btn-${question.id}-yes`}
                >
                  Oui
                </button>
                <button
                  type="button"
                  onClick={() => handleAnswerChange(question.id, false)}
                  className={`flex-1 h-12 rounded-xl border-2 font-medium transition-all ${
                    answers[question.id] === false
                      ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                      : 'border-slate-200 text-slate-600 hover:border-slate-300'
                  }`}
                  data-testid={`btn-${question.id}-no`}
                >
                  Non
                </button>
              </div>
            )}

            {question.type === 'multiselect' && (
              <div className="flex flex-wrap gap-2">
                {question.options.map((opt) => {
                  const isSelected = (answers[question.id] || []).includes(opt);
                  return (
                    <button
                      key={opt}
                      type="button"
                      onClick={() => handleMultiSelectToggle(question.id, opt)}
                      className={`px-4 py-2 rounded-lg border-2 text-sm font-medium transition-all ${
                        isSelected
                          ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                          : 'border-slate-200 text-slate-600 hover:border-slate-300'
                      }`}
                    >
                      {isSelected && <Check className="w-3 h-3 inline mr-1" />}
                      {opt}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        ))}
      </div>

      {questions.some(q => q.id === 'sells_alcohol') && answers['sells_alcohol'] === true && (
        <div className="bg-amber-50 rounded-xl p-4 flex items-start gap-3">
          <Info className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-amber-700">
            La vente d'alcool nécessite une licence. Vous pourrez télécharger votre licence à l'étape Documents.
          </p>
        </div>
      )}
    </div>
  );
}

function BasicInfoStep({ formData, updateFormData, onUpload, uploadingLogo, uploadingCover }) {
  const handleAddressSelect = (addressData) => {
    updateFormData('address', addressData.address || addressData.formatted_address);
    updateFormData('city', addressData.city);
    updateFormData('zipcode', addressData.zipcode);
    updateFormData('latitude', addressData.latitude);
    updateFormData('longitude', addressData.longitude);
  };

  const handleLogoChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert('Le fichier est trop lourd (max 5 MB)');
        return;
      }
      onUpload(file, 'logo');
    }
  };

  const handleCoverChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        alert('Le fichier est trop lourd (max 10 MB)');
        return;
      }
      onUpload(file, 'cover');
    }
  };

  return (
    <div className="space-y-6" data-testid="step-basic-info">
      <div className="text-center">
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2">
          Informations de base
        </h1>
        <p className="text-slate-500">
          Renseignez les informations essentielles de votre commerce
        </p>
      </div>

      {/* Upload Logo et Cover Image */}
      <div className="bg-white rounded-2xl border border-slate-100 p-6 space-y-5">
        <div className="space-y-4">
          <Label className="text-sm font-medium text-slate-700 flex items-center gap-2">
            <ImageIcon className="w-4 h-4 text-emerald-500" />
            Logo et image de couverture
          </Label>
          
          <div className="grid grid-cols-2 gap-4">
            {/* Logo Upload */}
            <div className="space-y-2">
              <span className="text-xs text-slate-500">Logo (recommandé: carré)</span>
              <label className={`relative block cursor-pointer ${uploadingLogo ? 'pointer-events-none' : ''}`}>
                <input 
                  type="file" 
                  accept="image/*" 
                  className="sr-only" 
                  onChange={handleLogoChange}
                  data-testid="input-logo"
                />
                <div className={`w-full aspect-square rounded-xl border-2 border-dashed flex flex-col items-center justify-center transition-all overflow-hidden ${
                  formData.logo 
                    ? 'border-emerald-300 bg-emerald-50' 
                    : 'border-slate-200 hover:border-emerald-400 hover:bg-emerald-50/50'
                }`}>
                  {uploadingLogo ? (
                    <Loader2 className="w-8 h-8 text-emerald-500 animate-spin" />
                  ) : formData.logo ? (
                    <img 
                      src={formData.logo} 
                      alt="Logo" 
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <>
                      <Upload className="w-6 h-6 text-slate-400 mb-1" />
                      <span className="text-xs text-slate-500">Logo</span>
                    </>
                  )}
                </div>
              </label>
              {formData.logo && (
                <button 
                  type="button"
                  onClick={() => updateFormData('logo', null)}
                  className="text-xs text-red-500 hover:text-red-600"
                >
                  Supprimer
                </button>
              )}
            </div>

            {/* Cover Image Upload */}
            <div className="space-y-2">
              <span className="text-xs text-slate-500">Couverture (format paysage)</span>
              <label className={`relative block cursor-pointer ${uploadingCover ? 'pointer-events-none' : ''}`}>
                <input 
                  type="file" 
                  accept="image/*" 
                  className="sr-only" 
                  onChange={handleCoverChange}
                  data-testid="input-cover"
                />
                <div className={`w-full aspect-video rounded-xl border-2 border-dashed flex flex-col items-center justify-center transition-all overflow-hidden ${
                  formData.cover_image 
                    ? 'border-emerald-300 bg-emerald-50' 
                    : 'border-slate-200 hover:border-emerald-400 hover:bg-emerald-50/50'
                }`}>
                  {uploadingCover ? (
                    <Loader2 className="w-8 h-8 text-emerald-500 animate-spin" />
                  ) : formData.cover_image ? (
                    <img 
                      src={formData.cover_image} 
                      alt="Couverture" 
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <>
                      <Upload className="w-6 h-6 text-slate-400 mb-1" />
                      <span className="text-xs text-slate-500">Couverture</span>
                    </>
                  )}
                </div>
              </label>
              {formData.cover_image && (
                <button 
                  type="button"
                  onClick={() => updateFormData('cover_image', null)}
                  className="text-xs text-red-500 hover:text-red-600"
                >
                  Supprimer
                </button>
              )}
            </div>
          </div>
          <p className="text-xs text-slate-400">
            Les images seront optimisées automatiquement. Max 5 MB pour le logo, 10 MB pour la couverture.
          </p>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 p-6 space-y-5">
        <div>
          <Label className="text-sm font-medium text-slate-700">
            Nom du commerce <span className="text-red-500">*</span>
          </Label>
          <Input
            type="text"
            value={formData.name}
            onChange={(e) => updateFormData('name', e.target.value)}
            placeholder="Boulangerie du Marché"
            className="mt-1.5 h-12 rounded-xl"
            data-testid="input-business-name"
          />
        </div>

        <div className="space-y-4">
          <Label className="text-sm font-medium text-slate-700">
            Adresse complète <span className="text-red-500">*</span>
          </Label>
          <AddressAutocomplete
            value={formData.address}
            onChange={(value) => updateFormData('address', value)}
            onSelect={handleAddressSelect}
            placeholder="Rue, numéro..."
          />
          <div className="grid grid-cols-2 gap-4">
            <Input
              type="text"
              value={formData.zipcode}
              onChange={(e) => updateFormData('zipcode', e.target.value)}
              placeholder="Code postal"
              className="h-12 rounded-xl"
              data-testid="input-zipcode"
            />
            <Input
              type="text"
              value={formData.city}
              onChange={(e) => updateFormData('city', e.target.value)}
              placeholder="Ville"
              className="h-12 rounded-xl"
              data-testid="input-city"
            />
          </div>
        </div>

        <div>
          <Label className="text-sm font-medium text-slate-700">
            Téléphone du commerce <span className="text-red-500">*</span>
          </Label>
          <div className="relative mt-1.5">
            <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <Input
              type="tel"
              value={formData.phone}
              onChange={(e) => updateFormData('phone', e.target.value)}
              placeholder="+33 1 23 45 67 89"
              className="h-12 rounded-xl pl-10"
              data-testid="input-phone"
            />
          </div>
        </div>

        <div>
          <Label className="text-sm font-medium text-slate-700">
            Email du commerce <span className="text-red-500">*</span>
          </Label>
          <div className="relative mt-1.5">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <Input
              type="email"
              value={formData.email}
              onChange={(e) => updateFormData('email', e.target.value)}
              placeholder="contact@votrecommerce.fr"
              className="h-12 rounded-xl pl-10"
              data-testid="input-email"
            />
          </div>
        </div>

        <div className="border-t border-slate-100 pt-5 space-y-4">
          <p className="text-sm text-slate-500 flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-slate-300"></span>
            Champs optionnels
          </p>

          <div>
            <Label className="text-sm font-medium text-slate-700">
              Site web <span className="text-slate-400 font-normal">(optionnel)</span>
            </Label>
            <div className="relative mt-1.5">
              <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                type="url"
                value={formData.website}
                onChange={(e) => updateFormData('website', e.target.value)}
                placeholder="https://www.votresite.fr"
                className="h-12 rounded-xl pl-10"
                data-testid="input-website"
              />
            </div>
          </div>

          <div>
            <div className="flex items-center gap-2">
              <Label className="text-sm font-medium text-slate-700">
                SIRET / SIREN <span className="text-slate-400 font-normal">(optionnel)</span>
              </Label>
              <div className="group relative">
                <Info className="w-4 h-4 text-slate-400 cursor-help" />
                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 p-3 bg-slate-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                  Le numéro SIRET permet de vérifier l'existence légale de votre entreprise.
                </div>
              </div>
            </div>
            <Input
              type="text"
              value={formData.siret}
              onChange={(e) => updateFormData('siret', e.target.value)}
              placeholder="123 456 789 00012"
              className="mt-1.5 h-12 rounded-xl"
              data-testid="input-siret"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function OperationsStep({ formData, updateFormData }) {
  const togglePayment = (id) => {
    const current = formData.accepted_payment_methods;
    if (current.includes(id)) {
      if (current.length > 1) {
        updateFormData('accepted_payment_methods', current.filter(p => p !== id));
      }
    } else {
      updateFormData('accepted_payment_methods', [...current, id]);
    }
  };

  const toggleClosedDay = (dayId) => {
    const current = formData.closed_days;
    if (current.includes(dayId)) {
      updateFormData('closed_days', current.filter(d => d !== dayId));
    } else {
      updateFormData('closed_days', [...current, dayId]);
    }
  };

  const updateDayHours = (dayId, field, value) => {
    const current = formData.opening_hours;
    updateFormData('opening_hours', {
      ...current,
      [dayId]: { ...current[dayId], [field]: value }
    });
  };

  return (
    <div className="space-y-6" data-testid="step-operations">
      <div className="text-center">
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2">
          Détails opérationnels
        </h1>
        <p className="text-slate-500">
          Configurez vos horaires et moyens de paiement
        </p>
      </div>

      {/* Horaires */}
      <div className="bg-white rounded-2xl border border-slate-100 p-6 space-y-5">
        <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
          <Clock className="w-5 h-5 text-emerald-500" />
          Horaires d'ouverture
        </h2>

        <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
          <Label className="text-sm text-slate-600 cursor-pointer">
            Mêmes horaires toute la semaine
          </Label>
          <Checkbox
            checked={formData.same_hours_all_week}
            onCheckedChange={(checked) => updateFormData('same_hours_all_week', checked)}
            data-testid="checkbox-same-hours"
          />
        </div>

        {formData.same_hours_all_week ? (
          <div className="flex items-center gap-3">
            <div className="flex-1">
              <Label className="text-xs text-slate-500 mb-1 block">Ouverture</Label>
              <Input 
                type="time" 
                value={formData.default_open}
                onChange={(e) => updateFormData('default_open', e.target.value)}
                className="h-11 rounded-lg" 
              />
            </div>
            <span className="text-slate-400 mt-5">→</span>
            <div className="flex-1">
              <Label className="text-xs text-slate-500 mb-1 block">Fermeture</Label>
              <Input 
                type="time" 
                value={formData.default_close}
                onChange={(e) => updateFormData('default_close', e.target.value)}
                className="h-11 rounded-lg" 
              />
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {DAYS.map((day) => (
              <div key={day.id} className="flex items-center gap-3">
                <div className="w-24 flex items-center gap-2">
                  <Checkbox
                    checked={formData.closed_days.includes(day.id)}
                    onCheckedChange={() => toggleClosedDay(day.id)}
                  />
                  <span className={`text-sm ${formData.closed_days.includes(day.id) ? 'text-slate-400 line-through' : 'text-slate-700 font-medium'}`}>
                    {day.label}
                  </span>
                </div>
                {!formData.closed_days.includes(day.id) && (
                  <>
                    <Input 
                      type="time" 
                      value={formData.opening_hours[day.id]?.open || '09:00'}
                      onChange={(e) => updateDayHours(day.id, 'open', e.target.value)}
                      className="flex-1 h-10 rounded-lg text-sm" 
                    />
                    <span className="text-slate-400 text-sm">→</span>
                    <Input 
                      type="time" 
                      value={formData.opening_hours[day.id]?.close || '19:00'}
                      onChange={(e) => updateDayHours(day.id, 'close', e.target.value)}
                      className="flex-1 h-10 rounded-lg text-sm"
                    />
                  </>
                )}
                {formData.closed_days.includes(day.id) && (
                  <span className="text-sm text-red-500 italic">Fermé</span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Paiements */}
      <div className="bg-white rounded-2xl border border-slate-100 p-6 space-y-4">
        <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
          <CreditCard className="w-5 h-5 text-emerald-500" />
          Moyens de paiement acceptés au comptoir
          <span className="text-red-500">*</span>
        </h2>
        <p className="text-sm text-slate-500">Sélectionnez au moins un moyen de paiement</p>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {PAYMENT_METHODS.map((method) => {
            const isSelected = formData.accepted_payment_methods.includes(method.id);
            return (
              <button
                key={method.id}
                type="button"
                onClick={() => togglePayment(method.id)}
                className={`flex items-center gap-3 p-3 border-2 rounded-xl transition-all ${
                  isSelected 
                    ? 'border-emerald-500 bg-emerald-50' 
                    : 'border-slate-200 hover:border-slate-300'
                }`}
                data-testid={`payment-${method.id}`}
              >
                {typeof method.icon === 'string' ? (
                  <span className="text-xl">{method.icon}</span>
                ) : (
                  <method.icon className="w-5 h-5 text-slate-600" />
                )}
                <span className={`text-sm font-medium ${isSelected ? 'text-emerald-700' : 'text-slate-700'}`}>
                  {method.label}
                </span>
                {isSelected && <Check className="w-4 h-4 text-emerald-500 ml-auto" />}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function AccountStep({ formData, updateFormData, showPassword, setShowPassword }) {
  const [emailStatus, setEmailStatus] = useState(null); // null, 'checking', 'available', 'taken'
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const checkEmail = async (email) => {
    if (!email || !email.includes('@') || !email.includes('.')) {
      setEmailStatus(null);
      return;
    }
    setEmailStatus('checking');
    try {
      const res = await axios.post(`${API_URL}/api/auth/check-email`, { email });
      setEmailStatus(res.data.exists ? 'taken' : 'available');
    } catch {
      setEmailStatus(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="step-account">
      <div className="text-center">
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2">
          Compte responsable
        </h1>
        <p className="text-slate-500">
          Informations du responsable principal du commerce
        </p>
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 p-6 space-y-5">
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-sm font-medium text-slate-700">
              Prenom <span className="text-red-500">*</span>
            </Label>
            <Input
              type="text"
              value={formData.manager_first_name}
              onChange={(e) => updateFormData('manager_first_name', e.target.value)}
              placeholder="Jean"
              className="mt-1.5 h-12 rounded-xl"
              data-testid="input-firstname"
            />
          </div>
          <div>
            <Label className="text-sm font-medium text-slate-700">
              Nom <span className="text-red-500">*</span>
            </Label>
            <Input
              type="text"
              value={formData.manager_last_name}
              onChange={(e) => updateFormData('manager_last_name', e.target.value)}
              placeholder="Dupont"
              className="mt-1.5 h-12 rounded-xl"
              data-testid="input-lastname"
            />
          </div>
        </div>

        <div>
          <Label className="text-sm font-medium text-slate-700">
            Email personnel <span className="text-red-500">*</span>
          </Label>
          <div className="relative mt-1.5">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <Input
              type="email"
              value={formData.manager_email}
              onChange={(e) => {
                updateFormData('manager_email', e.target.value);
                checkEmail(e.target.value);
              }}
              placeholder="jean.dupont@email.com"
              className={`h-12 rounded-xl pl-10 ${emailStatus === 'taken' ? 'border-red-400 focus:border-red-500' : ''}`}
              data-testid="input-personal-email"
            />
          </div>
          {emailStatus === 'taken' && (
            <p className="text-sm text-red-500 mt-1" data-testid="email-taken-error">
              Cette adresse email est deja utilisee. Essayez de vous connecter ou utilisez une autre adresse.
            </p>
          )}
          {emailStatus === 'available' && formData.manager_email && (
            <p className="text-sm text-emerald-500 mt-1">Email disponible</p>
          )}
        </div>

        <div>
          <Label className="text-sm font-medium text-slate-700">
            Téléphone personnel <span className="text-red-500">*</span>
          </Label>
          <div className="relative mt-1.5">
            <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <Input
              type="tel"
              value={formData.manager_phone}
              onChange={(e) => updateFormData('manager_phone', e.target.value)}
              placeholder="+33 6 12 34 56 78"
              className="h-12 rounded-xl pl-10"
              data-testid="input-personal-phone"
            />
          </div>
        </div>

        <div>
          <Label className="text-sm font-medium text-slate-700">
            Rôle dans l'entreprise <span className="text-red-500">*</span>
          </Label>
          <select 
            value={formData.manager_role}
            onChange={(e) => updateFormData('manager_role', e.target.value)}
            className="mt-1.5 w-full h-12 rounded-xl border border-slate-200 px-4 bg-white focus:border-emerald-500 focus:ring-emerald-500"
            data-testid="select-role"
          >
            <option value="">Sélectionnez un rôle</option>
            <option value="owner">Propriétaire</option>
            <option value="manager">Gérant</option>
            <option value="staff_lead">Responsable d'équipe</option>
            <option value="other">Autre</option>
          </select>
        </div>

        <div>
          <Label className="text-sm font-medium text-slate-700">
            Mot de passe <span className="text-red-500">*</span>
          </Label>
          <div className="relative mt-1.5">
            <Input
              type={showPassword ? 'text' : 'password'}
              value={formData.password}
              onChange={(e) => updateFormData('password', e.target.value)}
              placeholder="Minimum 8 caractères"
              className="h-12 rounded-xl pr-12"
              data-testid="input-password"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
            >
              {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>
          <p className="text-xs text-slate-500 mt-1.5">
            Au moins 8 caractères, une majuscule et un chiffre
          </p>
        </div>

        <div>
          <Label className="text-sm font-medium text-slate-700">
            Confirmer le mot de passe <span className="text-red-500">*</span>
          </Label>
          <Input
            type="password"
            value={formData.confirm_password}
            onChange={(e) => updateFormData('confirm_password', e.target.value)}
            placeholder="••••••••"
            className="mt-1.5 h-12 rounded-xl"
            data-testid="input-confirm-password"
          />
        </div>
      </div>
    </div>
  );
}

function DocumentsStep({ formData, updateFormData }) {
  return (
    <div className="space-y-6" data-testid="step-documents">
      <div className="text-center">
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2">
          Documents & conformité
        </h1>
        <p className="text-slate-500">
          Téléchargez les documents nécessaires pour votre activité
        </p>
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 p-6 space-y-5">
        <div>
          <Label className="text-sm font-medium text-slate-700 flex items-center gap-2">
            <Wine className="w-4 h-4 text-purple-500" />
            Licence de vente d'alcool
            <span className="text-slate-400 font-normal">(si applicable)</span>
          </Label>
          <div className="mt-2 border-2 border-dashed border-slate-200 rounded-xl p-6 text-center hover:border-purple-300 transition-colors cursor-pointer" data-testid="upload-alcohol-license">
            <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
            <p className="text-sm text-slate-600">Télécharger la licence</p>
            <p className="text-xs text-slate-400 mt-1">PDF, PNG, JPG</p>
          </div>
        </div>

        <div>
          <Label className="text-sm font-medium text-slate-700">
            Autres documents
            <span className="text-slate-400 font-normal ml-2">(optionnel)</span>
          </Label>
          <div className="mt-2 border-2 border-dashed border-slate-200 rounded-xl p-6 text-center hover:border-slate-300 transition-colors cursor-pointer" data-testid="upload-other">
            <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
            <p className="text-sm text-slate-600">Ajouter d'autres documents</p>
            <p className="text-xs text-slate-400 mt-1">Kbis, attestation d'assurance, etc.</p>
          </div>
        </div>
      </div>

      <div className="bg-emerald-50 rounded-xl p-4 flex items-start gap-3">
        <Info className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
        <p className="text-sm text-emerald-700">
          Vous pourrez créer votre menu/carte directement depuis votre dashboard après l'inscription.
        </p>
      </div>

      <div className="bg-amber-50 rounded-xl p-4 flex items-start gap-3">
        <Info className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
        <p className="text-sm text-amber-700">
          Vous pouvez passer cette étape et ajouter les documents plus tard depuis votre espace.
        </p>
      </div>
    </div>
  );
}

function ConfirmationStep({ formData, updateFormData }) {
  const shopTypeLabel = SHOP_TYPES.find(t => t.id === formData.shop_type)?.label || 'Commerce';
  
  return (
    <div className="space-y-6" data-testid="step-confirmation">
      <div className="text-center">
        <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
          <CheckCircle2 className="w-8 h-8 text-emerald-600" />
        </div>
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2">
          Récapitulatif
        </h1>
        <p className="text-slate-500">
          Vérifiez vos informations avant de finaliser
        </p>
      </div>

      <div className="space-y-4">
        <SummaryCard 
          title="Type de commerce" 
          icon={Building2}
          items={[{ label: 'Type', value: shopTypeLabel }]}
        />

        <SummaryCard 
          title="Informations de base" 
          icon={MapPin}
          items={[
            { label: 'Nom', value: formData.name || formData.shop_name },
            { label: 'Adresse', value: `${formData.address}, ${formData.zipcode} ${formData.city}` },
            { label: 'Téléphone', value: formData.phone },
            { label: 'Email', value: formData.email || formData.contact_email },
          ]}
        />

        <SummaryCard 
          title="Détails opérationnels" 
          icon={Clock}
          items={[
            { label: 'Paiements', value: formData.accepted_payment_methods.map(p => 
              PAYMENT_METHODS.find(m => m.id === p)?.label
            ).join(', ') },
          ]}
        />

        <SummaryCard 
          title="Responsable" 
          icon={User}
          items={[
            { label: 'Nom', value: `${formData.manager_first_name} ${formData.manager_last_name}` },
            { label: 'Email', value: formData.manager_email },
          ]}
        />
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 p-6">
        <div className="flex items-start gap-3">
          <Checkbox 
            id="confirm" 
            checked={formData.confirmed}
            onCheckedChange={(checked) => updateFormData('confirmed', checked)}
            className="mt-1" 
            data-testid="checkbox-confirm" 
          />
          <label htmlFor="confirm" className="text-sm text-slate-600 cursor-pointer">
            J'ai vérifié que toutes les informations sont correctes et j'accepte les{' '}
            <a href="/cgu" className="text-emerald-600 hover:underline">conditions générales d'utilisation</a>
            {' '}et la{' '}
            <a href="/politique-de-confidentialite" className="text-emerald-600 hover:underline">politique de confidentialité</a>.
          </label>
        </div>
      </div>

      <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl border border-emerald-100 p-6 space-y-4">
        <h3 className="font-semibold text-slate-900 flex items-center gap-2">
          <Calendar className="w-5 h-5 text-emerald-600" />
          Session d'accompagnement (optionnel)
        </h3>
        <p className="text-sm text-slate-600">
          Besoin d'aide pour démarrer ? Planifiez un appel avec notre équipe.
        </p>
        <div className="flex flex-wrap gap-3">
          <Button 
            variant={formData.onboarding_call_type === 'call' ? 'default' : 'outline'} 
            className={`h-10 rounded-lg ${formData.onboarding_call_type === 'call' ? 'bg-emerald-500' : ''}`}
            onClick={() => updateFormData('onboarding_call_type', 'call')}
          >
            <Phone className="w-4 h-4 mr-2" />
            Appel téléphonique
          </Button>
          <Button 
            variant={formData.onboarding_call_type === 'whatsapp' ? 'default' : 'outline'} 
            className={`h-10 rounded-lg ${formData.onboarding_call_type === 'whatsapp' ? 'bg-emerald-500' : ''}`}
            onClick={() => updateFormData('onboarding_call_type', 'whatsapp')}
          >
            <MessageCircle className="w-4 h-4 mr-2" />
            WhatsApp
          </Button>
          <Button 
            variant={formData.onboarding_call_type === 'video' ? 'default' : 'outline'} 
            className={`h-10 rounded-lg ${formData.onboarding_call_type === 'video' ? 'bg-emerald-500' : ''}`}
            onClick={() => updateFormData('onboarding_call_type', 'video')}
          >
            <Video className="w-4 h-4 mr-2" />
            Visio
          </Button>
        </div>
        <Button 
          variant="ghost" 
          className="text-sm text-slate-500 h-auto p-0 hover:text-slate-700"
          onClick={() => updateFormData('onboarding_call_type', null)}
        >
          Passer cette étape →
        </Button>
      </div>
    </div>
  );
}

function SummaryCard({ title, icon: Icon, items }) {
  return (
    <div className="bg-white rounded-xl border border-slate-100 p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-slate-900 flex items-center gap-2">
          <Icon className="w-4 h-4 text-emerald-500" />
          {title}
        </h3>
      </div>
      <div className="space-y-2">
        {items.map((item, index) => (
          <div key={index} className="flex justify-between text-sm">
            <span className="text-slate-500">{item.label}</span>
            <span className="text-slate-700 font-medium text-right max-w-[60%]">{item.value || '-'}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
